import { Col, Row } from 'antd'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { GlobalRequest } from '~/components/global-request'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { PAGE_SIZE } from '~/configs'
import { GET_FOLLOWING_LIST } from '~/graphqls/gqls'
import { UserCard } from '~/components/business/UserCard'
import {
  followList,
  followListVariables,
  followList_followList_edges_node,
} from '~/graphqls/types'
import useInfiniteScroll from '~/hooks/useInfiniteScroll'
import { Layout } from '~/layouts/default'
import { getGraphData, serverSideCatch } from '~/utils'
import MyTabs from '~/components/ui/MyTabs'
import { SEOHead } from '~/components/business/SEOHead'

const tabsData = [
  {
    id: 'following',
    name: '关注',
  },
  {
    id: 'follower',
    name: '粉丝',
  },
]

interface Props {
  hasNextPage: boolean
  list: followList_followList_edges_node[]
}

const Following = ({ list: followingList, hasNextPage: nextPage }: Props) => {
  const router = useRouter()
  const uid = Number(router.query.id)
  const { InfiniteScollComp, list } = useInfiniteScroll<
    followList_followList_edges_node,
    followList,
    followListVariables
  >({
    queryDoc: GET_FOLLOWING_LIST,
    extraQueryParams: {
      param: {
        uid,
      },
    },
    initData: followingList,
    hasNextPage: nextPage,
    hasNextPageExtractor: data => {
      return data.data?.followList?.pageInfo?.hasNextPage || false
    },
    dataExtractor: data => {
      return getGraphData<followList_followList_edges_node>(
        data.data.followList?.edges || []
      )
    },
    dataUniqueBy: (it: followList_followList_edges_node) => it?.base?.uid,
  })

  const onTabsChange = (item: any) => {
    const newPath = router.asPath
      .split('/')
      .slice(0, -1)
      .concat(item.id)
      .join('/')
    if (newPath !== router.asPath) {
      router.replace(newPath)
    }
  }

  return (
    <Layout>
      <SEOHead title="我的关注" />
      <MyContainer>
        <MyTabs
          className="pb-3 pt-3 sticky top-14 bg-neutral-100 z-[998] text-base"
          active={'following'}
          items={tabsData}
          onChange={onTabsChange}
        />
        {list.length > 0 ? (
          <>
            <Row gutter={32}>
              {list?.map(it => (
                <Col span={6} key={it.base?.uid}>
                  <UserCard data={it} />
                </Col>
              ))}
            </Row>
            {InfiniteScollComp}
          </>
        ) : (
          <MyEmpty
            description="尚未关注任何人"
            image="/images/empty/following.png"
          />
        )}
      </MyContainer>
    </Layout>
  )
}

export default Following

export const getServerSideProps: GetServerSideProps = async context => {
  return await serverSideCatch(async () => {
    const { initialState, client } = await GlobalRequest(context)
    const uid = Number(context.params?.id)

    if (Number.isNaN(uid)) {
      return {
        notFound: true,
      }
    }

    const [res] = await Promise.all([
      client.query<followList, followListVariables>({
        query: GET_FOLLOWING_LIST,
        variables: {
          param: {
            uid,
          },
          offset: 0,
          first: PAGE_SIZE,
        },
        fetchPolicy: 'no-cache',
      }),
    ])

    return {
      props: {
        initialState,
        hasNextPage: res?.data?.followList?.pageInfo?.hasNextPage || false,
        list: getGraphData(res?.data?.followList?.edges || []),
      },
    }
  })
}
